/**
 * Miskulin Technologies - License Key Utility Module
 *
 * Centralized license validation, activation, and management for all IQ apps.
 * Import this in any app:  import { checkLicense, activateLicense } from './licenseUtils';
 *
 * License key format: [PREFIX]-[XXXX]-[XXXX]-[XXXX]
 * Example: FH-A3K9-B7M2-X4P1  (FH = FarmHandIQ)
 */

import { supabase } from './supabase';

// ============================================================
// APP PREFIX MAP - Used when generating keys
// ============================================================
const APP_PREFIXES = {
  croppatrol: 'FH',
  haulsync: 'FL',
  'directional-ops': 'DI',
  lotsync: 'MO',
  nursevault: 'NQ',
  'nursevault-ca': 'NC',
  'vantage-ops': 'SC',
  budledger: 'CB',
  oresync: 'MN',
  vactraq: 'VH',
  glowdesk: 'BQ',
  closingdesk: 'RE',
  wrenchsync: 'MC',
  treadops: 'TS',
  herdledger: 'CT',
  wellnessiq: 'WL',
  'insurance-broker-iq': 'IB',
  mtiq: 'MT',
  stagevault: 'EV',
  formgenius: 'PW',
  iod: 'IO',
  ironledger: 'IL',
  'croppatrol-broda': 'BR',
  'hoist-control': 'HC',
  rigwarden: 'RW',
  'innovation-hub': 'IH',
  all: 'UN', // Universal key
};

// ============================================================
// PLAN DURATIONS (in days) - defaults when creating keys
// ============================================================
const PLAN_DEFAULTS = {
  trial: 14,
  monthly: 30,
  annual: 365,
  lifetime: null, // No expiry
  enterprise: 365,
};

// ============================================================
// KEY FORMAT VALIDATION
// Format: XX-XXXX-XXXX-XXXX (2-3 letter prefix, then 3 groups of 4 alphanumeric)
// ============================================================
const KEY_REGEX = /^[A-Z]{2,3}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

/**
 * Validate a license key format
 * @param {string} key - The license key to validate
 * @returns {boolean}
 */
export function isValidKeyFormat(key) {
  if (!key || typeof key !== 'string') return false;
  return KEY_REGEX.test(key.trim().toUpperCase());
}

/**
 * Generate a random 4-character alphanumeric block
 * @returns {string}
 */
function randomBlock() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No I,O,0,1 to avoid confusion
  let block = '';
  for (let i = 0; i < 4; i++) {
    block += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return block;
}

/**
 * Generate a formatted license key string
 * @param {string} appSlug - The app slug (e.g. "farmhandiq") or "all"
 * @returns {string} - Formatted key like "FH-A3K9-B7M2-X4P1"
 */
export function generateKeyString(appSlug = 'all') {
  const prefix = APP_PREFIXES[appSlug] || 'UN';
  return `${prefix}-${randomBlock()}-${randomBlock()}-${randomBlock()}`;
}

// ============================================================
// CHECK LICENSE
// Validates if the current user has an active, non-expired license
// ============================================================

/**
 * Check if a user has a valid license for a specific app
 * @param {string} userEmail - The user's email address
 * @param {string} appSlug - The app identifier (e.g. "farmhandiq")
 * @returns {Promise<{valid: boolean, plan?: string, expiresAt?: string, daysLeft?: number, licenseKey?: string, companyName?: string, message?: string}>}
 */
export async function checkLicense(userEmail, appSlug) {
  try {
    if (!userEmail || !appSlug) {
      return { valid: false, message: 'Missing user email or app identifier.' };
    }

    // Look up active activations for this user
    const { data: activations, error: actError } = await supabase
      .from('license_activations')
      .select(`
        id,
        is_active,
        last_verified_at,
        license_key_id,
        license_keys (
          id,
          license_key,
          app_slug,
          plan_type,
          is_active,
          expires_at,
          company_name
        )
      `)
      .eq('user_email', userEmail.toLowerCase())
      .eq('is_active', true);

    if (actError) {
      console.error('License check error:', actError);
      return { valid: false, message: 'Unable to verify license. Please try again.' };
    }

    if (!activations || activations.length === 0) {
      return { valid: false, message: 'No active license found for this account.' };
    }

    // Find a valid activation that matches this app (or is universal)
    for (const activation of activations) {
      const key = activation.license_keys;
      if (!key) continue;

      // Check app match
      const appMatch = key.app_slug === appSlug || key.app_slug === 'all';
      if (!appMatch) continue;

      // Check key is active
      if (!key.is_active) continue;

      // Check expiration
      if (key.expires_at) {
        const expiresAt = new Date(key.expires_at);
        const now = new Date();
        if (expiresAt <= now) {
          continue; // This key is expired, check next
        }

        const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

        // Update last_verified_at timestamp
        await supabase
          .from('license_activations')
          .update({ last_verified_at: new Date().toISOString() })
          .eq('id', activation.id);

        return {
          valid: true,
          plan: key.plan_type,
          expiresAt: key.expires_at,
          daysLeft,
          licenseKey: key.license_key,
          companyName: key.company_name,
        };
      }

      // No expiration (lifetime key)
      await supabase
        .from('license_activations')
        .update({ last_verified_at: new Date().toISOString() })
        .eq('id', activation.id);

      return {
        valid: true,
        plan: key.plan_type,
        expiresAt: null,
        daysLeft: 99999,
        licenseKey: key.license_key,
        companyName: key.company_name,
      };
    }

    // No valid matching key found
    return { valid: false, message: 'Your license has expired or is not valid for this application.' };

  } catch (err) {
    console.error('License check exception:', err);
    return { valid: false, message: 'License verification failed. Please contact support.' };
  }
}

// ============================================================
// ACTIVATE LICENSE
// Links a license key to a user account
// ============================================================

/**
 * Activate a license key for a user
 * @param {string} licenseKey - The key entered by the user (e.g. "FH-A3K9-B7M2-X4P1")
 * @param {string} userEmail - The user's email
 * @param {string} appSlug - The app identifier
 * @returns {Promise<{success: boolean, message: string, plan?: string, expiresAt?: string}>}
 */
export async function activateLicense(licenseKey, userEmail, appSlug) {
  try {
    if (!licenseKey || !userEmail || !appSlug) {
      return { success: false, message: 'Missing required information.' };
    }

    const cleanKey = licenseKey.trim().toUpperCase();

    // 1. Validate key format
    if (!isValidKeyFormat(cleanKey)) {
      return { success: false, message: 'Invalid key format. Expected format: XX-XXXX-XXXX-XXXX' };
    }

    // 2. Look up the key in the database
    const { data: keyRecord, error: keyError } = await supabase
      .from('license_keys')
      .select('*')
      .eq('license_key', cleanKey)
      .single();

    if (keyError || !keyRecord) {
      return { success: false, message: 'License key not found. Please check and try again.' };
    }

    // 3. Check key is active
    if (!keyRecord.is_active) {
      return { success: false, message: 'This license key has been revoked or deactivated.' };
    }

    // 4. Check key matches the app (or is universal)
    if (keyRecord.app_slug !== appSlug && keyRecord.app_slug !== 'all') {
      return { success: false, message: 'This license key is not valid for this application.' };
    }

    // 5. Check expiration
    if (keyRecord.expires_at) {
      const expiresAt = new Date(keyRecord.expires_at);
      if (expiresAt <= new Date()) {
        return { success: false, message: 'This license key has expired.' };
      }
    }

    // 6. Check max users not exceeded
    const { count, error: countError } = await supabase
      .from('license_activations')
      .select('*', { count: 'exact', head: true })
      .eq('license_key_id', keyRecord.id)
      .eq('is_active', true);

    if (countError) {
      return { success: false, message: 'Unable to verify available seats. Please try again.' };
    }

    // max_users of 0 or -1 means unlimited
    if (keyRecord.max_users > 0 && (count || 0) >= keyRecord.max_users) {
      return { success: false, message: `This key has reached its maximum of ${keyRecord.max_users} user(s). Contact your administrator.` };
    }

    // 7. Check if user already activated this key
    const { data: existing } = await supabase
      .from('license_activations')
      .select('id, is_active')
      .eq('license_key_id', keyRecord.id)
      .eq('user_email', userEmail.toLowerCase())
      .single();

    if (existing) {
      if (existing.is_active) {
        return { success: true, message: 'This key is already activated for your account.', plan: keyRecord.plan_type, expiresAt: keyRecord.expires_at };
      }
      // Reactivate
      await supabase
        .from('license_activations')
        .update({ is_active: true, last_verified_at: new Date().toISOString() })
        .eq('id', existing.id);
    } else {
      // 8. Create activation record
      const { error: insertError } = await supabase
        .from('license_activations')
        .insert({
          license_key_id: keyRecord.id,
          user_email: userEmail.toLowerCase(),
          activated_at: new Date().toISOString(),
          last_verified_at: new Date().toISOString(),
          is_active: true,
        });

      if (insertError) {
        console.error('Activation insert error:', insertError);
        return { success: false, message: 'Failed to activate key. Please try again.' };
      }
    }

    // 9. Set user role based on key's assigned_role
    if (keyRecord.assigned_role) {
      await supabase
        .from('users')
        .update({ role: keyRecord.assigned_role })
        .eq('email', userEmail.toLowerCase());
    }

    // 10. Write audit log
    await supabase.from('license_audit_log').insert({
      license_key_id: keyRecord.id,
      action: 'activated',
      performed_by: userEmail,
      details: `Activated for ${appSlug} by ${userEmail} | Role: ${keyRecord.assigned_role || 'unchanged'}`,
    });

    return {
      success: true,
      message: 'License activated successfully!',
      plan: keyRecord.plan_type,
      expiresAt: keyRecord.expires_at,
      assignedRole: keyRecord.assigned_role,
    };

  } catch (err) {
    console.error('Activation exception:', err);
    return { success: false, message: 'An unexpected error occurred. Please try again.' };
  }
}

// ============================================================
// GENERATE LICENSE KEY (Admin only)
// Creates a new key in the database
// ============================================================

/**
 * Generate a new license key
 * @param {Object} options
 * @param {string} options.appSlug - App identifier or "all"
 * @param {string} options.planType - "trial" | "monthly" | "annual" | "lifetime" | "enterprise"
 * @param {number} options.maxUsers - Maximum activations (1 = individual, 0 = unlimited)
 * @param {number|null} options.expiresInDays - Days until expiry (null for lifetime)
 * @param {string} options.companyName - Customer company name
 * @param {string} options.contactEmail - Customer contact email
 * @param {string} options.notes - Optional notes
 * @param {string} options.createdBy - Email of admin creating the key
 * @param {string} options.assignedRole - Role to assign when this key is activated (default: 'technician')
 * @returns {Promise<{success: boolean, key?: string, keyId?: string, message: string}>}
 */
export async function generateLicenseKey(options) {
  try {
    const {
      appSlug = 'all',
      planType = 'monthly',
      maxUsers = 1,
      expiresInDays,
      companyName = '',
      contactEmail = '',
      notes = '',
      createdBy = '',
      assignedRole = 'technician',
    } = options;

    // Generate unique key
    let keyString = generateKeyString(appSlug);

    // Ensure uniqueness (very unlikely collision but just in case)
    let attempts = 0;
    while (attempts < 5) {
      const { data: exists } = await supabase
        .from('license_keys')
        .select('id')
        .eq('license_key', keyString)
        .single();

      if (!exists) break;
      keyString = generateKeyString(appSlug);
      attempts++;
    }

    // Calculate expiry date
    let expiresAt = null;
    const days = expiresInDays !== undefined && expiresInDays !== null
      ? expiresInDays
      : PLAN_DEFAULTS[planType];

    if (days !== null && days !== undefined) {
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + days);
      expiresAt = expiry.toISOString();
    }

    // Insert into database
    const { data, error } = await supabase
      .from('license_keys')
      .insert({
        license_key: keyString,
        app_slug: appSlug,
        plan_type: planType,
        max_users: maxUsers,
        expires_at: expiresAt,
        is_active: true,
        created_by: createdBy,
        company_name: companyName,
        contact_email: contactEmail,
        notes,
        assigned_role: assignedRole,
      })
      .select()
      .single();

    if (error) {
      console.error('Key generation error:', error);
      return { success: false, message: 'Failed to generate license key.' };
    }

    // Audit log
    await supabase.from('license_audit_log').insert({
      license_key_id: data.id,
      action: 'created',
      performed_by: createdBy,
      details: `${planType} key for ${appSlug} | ${companyName || 'No company'} | Max users: ${maxUsers}`,
    });

    return {
      success: true,
      key: keyString,
      keyId: data.id,
      expiresAt,
      message: `Key generated: ${keyString}`,
    };

  } catch (err) {
    console.error('Key generation exception:', err);
    return { success: false, message: 'An unexpected error occurred generating the key.' };
  }
}

// ============================================================
// GET ALL LICENSE KEYS (Admin)
// ============================================================

/**
 * Retrieve all license keys, optionally filtered by app
 * @param {string|null} appSlug - Filter by app, or null for all
 * @returns {Promise<{success: boolean, keys?: Array, message?: string}>}
 */
export async function getAllLicenseKeys(appSlug = null) {
  try {
    let query = supabase
      .from('license_keys')
      .select(`
        *,
        license_activations (
          id,
          user_email,
          is_active,
          activated_at,
          last_verified_at
        )
      `)
      .order('created_at', { ascending: false });

    if (appSlug) {
      query = query.eq('app_slug', appSlug);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Fetch keys error:', error);
      return { success: false, message: 'Failed to fetch license keys.' };
    }

    return { success: true, keys: data || [] };

  } catch (err) {
    console.error('Fetch keys exception:', err);
    return { success: false, message: 'An unexpected error occurred.' };
  }
}

// ============================================================
// REVOKE LICENSE KEY
// ============================================================

/**
 * Revoke/deactivate a license key
 * @param {string} keyId - The UUID of the license key
 * @param {string} performedBy - Email of admin performing the action
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function revokeLicenseKey(keyId, performedBy = '') {
  try {
    const { error } = await supabase
      .from('license_keys')
      .update({ is_active: false })
      .eq('id', keyId);

    if (error) {
      return { success: false, message: 'Failed to revoke key.' };
    }

    // Deactivate all activations for this key
    await supabase
      .from('license_activations')
      .update({ is_active: false })
      .eq('license_key_id', keyId);

    // Audit log
    await supabase.from('license_audit_log').insert({
      license_key_id: keyId,
      action: 'revoked',
      performed_by: performedBy,
      details: 'Key revoked and all activations deactivated',
    });

    return { success: true, message: 'License key revoked successfully.' };

  } catch (err) {
    console.error('Revoke error:', err);
    return { success: false, message: 'An unexpected error occurred.' };
  }
}

// ============================================================
// REACTIVATE LICENSE KEY
// ============================================================

/**
 * Reactivate a previously revoked key
 * @param {string} keyId - The UUID of the license key
 * @param {string} performedBy - Email of admin
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function reactivateLicenseKey(keyId, performedBy = '') {
  try {
    const { error } = await supabase
      .from('license_keys')
      .update({ is_active: true })
      .eq('id', keyId);

    if (error) {
      return { success: false, message: 'Failed to reactivate key.' };
    }

    await supabase.from('license_audit_log').insert({
      license_key_id: keyId,
      action: 'updated',
      performed_by: performedBy,
      details: 'Key reactivated',
    });

    return { success: true, message: 'License key reactivated.' };

  } catch (err) {
    console.error('Reactivate error:', err);
    return { success: false, message: 'An unexpected error occurred.' };
  }
}

// ============================================================
// RENEW LICENSE KEY
// ============================================================

/**
 * Extend a license key's expiration
 * @param {string} keyId - The UUID of the license key
 * @param {number} additionalDays - Number of days to add
 * @param {string} performedBy - Email of admin
 * @returns {Promise<{success: boolean, newExpiresAt?: string, message: string}>}
 */
export async function renewLicenseKey(keyId, additionalDays, performedBy = '') {
  try {
    // Get current key
    const { data: key, error: fetchError } = await supabase
      .from('license_keys')
      .select('expires_at, is_active')
      .eq('id', keyId)
      .single();

    if (fetchError || !key) {
      return { success: false, message: 'License key not found.' };
    }

    // Calculate new expiry
    let baseDate = key.expires_at ? new Date(key.expires_at) : new Date();
    // If the key was already expired, extend from now instead
    if (baseDate < new Date()) {
      baseDate = new Date();
    }
    baseDate.setDate(baseDate.getDate() + additionalDays);
    const newExpiresAt = baseDate.toISOString();

    // Update the key
    const { error: updateError } = await supabase
      .from('license_keys')
      .update({ expires_at: newExpiresAt, is_active: true })
      .eq('id', keyId);

    if (updateError) {
      return { success: false, message: 'Failed to renew key.' };
    }

    // Audit log
    await supabase.from('license_audit_log').insert({
      license_key_id: keyId,
      action: 'renewed',
      performed_by: performedBy,
      details: `Extended by ${additionalDays} days. New expiry: ${newExpiresAt}`,
    });

    return { success: true, newExpiresAt, message: `Key renewed. New expiration: ${new Date(newExpiresAt).toLocaleDateString()}` };

  } catch (err) {
    console.error('Renew error:', err);
    return { success: false, message: 'An unexpected error occurred.' };
  }
}

// ============================================================
// GET AUDIT LOG
// ============================================================

/**
 * Get the audit log for a specific key or all keys
 * @param {string|null} keyId - Filter by key ID, or null for all
 * @param {number} limit - Max entries to return
 * @returns {Promise<{success: boolean, entries?: Array, message?: string}>}
 */
export async function getAuditLog(keyId = null, limit = 100) {
  try {
    let query = supabase
      .from('license_audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (keyId) {
      query = query.eq('license_key_id', keyId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, message: 'Failed to fetch audit log.' };
    }

    return { success: true, entries: data || [] };

  } catch (err) {
    return { success: false, message: 'An unexpected error occurred.' };
  }
}

// ============================================================
// EXPORTS SUMMARY
// ============================================================
// checkLicense(userEmail, appSlug)       - Verify user license
// activateLicense(key, email, appSlug)   - Activate a key
// generateLicenseKey(options)            - Create new key (admin)
// getAllLicenseKeys(appSlug?)            - List all keys (admin)
// revokeLicenseKey(keyId, by)           - Revoke a key (admin)
// reactivateLicenseKey(keyId, by)       - Reactivate key (admin)
// renewLicenseKey(keyId, days, by)      - Extend expiry (admin)
// getAuditLog(keyId?, limit?)           - View audit log (admin)
// generateKeyString(appSlug)            - Generate key string
// isValidKeyFormat(key)                 - Validate key format
// APP_PREFIXES                          - App prefix map
// PLAN_DEFAULTS                         - Plan duration defaults
export { APP_PREFIXES, PLAN_DEFAULTS };
